import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { STAGE_LABEL, type PaymentRow, type TodayAction, type TodaySummary, type TodayUnitRow, type UnitStage } from './summary';
import type { DrawerActionType, PendingStep } from './workflowBoard';
import type { CostSummary } from './costs';

export const inr = (v: number): string => {
  const a = Math.abs(v);
  let s: string;
  if (a >= 1e7) s = (a / 1e7).toFixed(2).replace(/\.?0+$/, '') + ' Cr';
  else if (a >= 1e5) s = (a / 1e5).toFixed(1).replace(/\.0$/, '') + ' L';
  else s = Math.round(a).toLocaleString('en-IN');
  return (v < 0 ? '-' : '') + '₹' + s;
};

const shortDate = (d: Date) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

const reduceMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Counts a number up from zero once, for the headline figures. */
export function CountUp({ value, format = inr }: { value: number; format?: (v: number) => string }) {
  const [shown, setShown] = useState(value);
  const first = useRef(true);
  useEffect(() => {
    if (!first.current || reduceMotion()) {
      setShown(value);
      return;
    }
    first.current = false;
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / 1100);
      setShown(value * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span className="num">{format(shown)}</span>;
}

const ICONS: Record<string, React.ReactNode> = {
  up: <path d="M7 17 17 7M8 7h9v9" />,
  wallet: <><path d="M4 7h15a1 1 0 0 1 1 1v11H5a1 1 0 0 1-1-1z" /><path d="M4 7l11-3v3M16 13h1" /></>,
  coin: <><circle cx="12" cy="12" r="8" /><path d="M9 9h6M9 12h6M12 12l-3 4" /></>,
  gst: <><path d="M5 3h14v18H5z" /><path d="m9 15 6-6M9.5 9.5h0M14.5 14.5h0" /></>,
  home: <><path d="M4 21V9l8-6 8 6v12" /><path d="M9 21v-6h6v6" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  phone: <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />,
  bank: <path d="M3 10h18L12 4zM5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 20h18" />,
  doc: <><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5M10 13h6M10 17h6" /></>,
  alert: <><path d="M12 4 2.5 20h19z" /><path d="M12 10v4M12 17v.5" /></>,
  truck: <><path d="M3 6h11v10H3zM14 10h4l3 3v3h-7" /><circle cx="7" cy="18" r="2" /><circle cx="17" cy="18" r="2" /></>,
  tool: <path d="M14 6a4 4 0 0 0 5 5l-9 9-3-3 9-9a4 4 0 0 0-2-2" />,
};
export const Icon = ({ name }: { name: keyof typeof ICONS }) => (
  <svg className="ot-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name]}
  </svg>
);

type CardProps = { className?: string; index: number; children: React.ReactNode; label: string };
export function Card({ className = '', index, children, label }: CardProps) {
  const onMove = (e: React.PointerEvent<HTMLElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
  };
  return (
    <section className={`ot-card ${className}`} style={{ ['--i' as any]: index }} onPointerMove={onMove} aria-label={label}>
      {children}
    </section>
  );
}

const Head = ({ title, sub, onOpen }: { title: string; sub?: React.ReactNode; onOpen?: () => void }) => (
  <div className="ot-hd">
    <div>
      <h3>{title}</h3>
      {sub ? <p>{sub}</p> : null}
    </div>
    {onOpen ? (
      <button className="ot-go" onClick={onOpen} aria-label={`Open ${title}`}>
        <Icon name="up" />
      </button>
    ) : null}
  </div>
);

const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 1000) / 10 : 0);

export function HeroCard({ s, greeting, onRecord, onLedger }: { s: TodaySummary; greeting: string; onRecord: () => void; onLedger: () => void }) {
  const rp = pct(s.received, s.bookedValue);
  const today = s.receivedToday;
  return (
    <Card className="tint" index={0} label="Collection summary">
      <div className="ot-hd">
        <div>
          <h3 style={{ fontSize: 22, letterSpacing: '-.02em' }}>{greeting}</h3>
          <p style={{ fontSize: 15, maxWidth: '44ch' }}>
            {today.rows.length
              ? `${inr(today.amount)} came in today across ${today.rows.length} receipt${today.rows.length === 1 ? '' : 's'}.`
              : 'No receipts posted yet today.'}{' '}
            {inr(s.receivedThisMonth)} collected so far this month.
          </p>
        </div>
      </div>
      <div className="ot-lbl" style={{ marginTop: 18 }}>Collected so far</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, margin: '4px 0 14px' }}>
        <div className="ot-big"><CountUp value={s.received} /></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="ot-btn sm" onClick={onLedger}>Booking report</button>
          <button className="ot-btn sm dark" onClick={onRecord}><Icon name="plus" />Record receipt</button>
        </div>
      </div>
      <div className="ot-meter" style={{ height: 14 }}>
        <i className="m-acc" style={{ width: `${rp}%` }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginTop: 10, fontSize: 13 }}>
        <span className="muted">{rp}% of booked value</span>
        <span><span className="muted">Booked value</span> <b className="num">{inr(s.bookedValue)}</b></span>
      </div>
    </Card>
  );
}

type StatProps = { index: number; label: string; icon: keyof typeof ICONS; tone: string; value: number; valueFormat?: (v: number) => string; chip: React.ReactNode; fill: number; meter: string; rows: Array<[string, string]>; onOpen?: () => void };
export function StatCard({ index, label, icon, tone, value, valueFormat, chip, fill, meter, rows, onOpen }: StatProps) {
  return (
    <Card index={index} label={label}>
      <div className="ot-hd">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className={`ot-ibox c-${tone}`}><Icon name={icon} /></div>
          <h3 style={{ fontSize: 14, fontWeight: 500 }}>{label}</h3>
        </div>
        {onOpen ? <button className="ot-go" onClick={onOpen} aria-label={`Open ${label}`}><Icon name="up" /></button> : null}
      </div>
      <div className="ot-mid"><CountUp value={value} format={valueFormat} /></div>
      <div style={{ margin: '8px 0 14px' }}>{chip}</div>
      <div className="ot-meter thin"><i className={meter} style={{ width: `${Math.min(100, Math.max(0, fill))}%` }} /></div>
      <div style={{ marginTop: 10 }}>
        {rows.map(([k, v]) => (
          <div className="ot-kv" key={k}><span className="muted">{k}</span><b className="num">{v}</b></div>
        ))}
      </div>
    </Card>
  );
}

const niceStep = (x: number) => {
  if (x <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(x)));
  const f = x / p;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p;
};

export function CollectionsChart({ s, index, onOpen }: { s: TodaySummary; index: number; onOpen: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  const [mode, setMode] = useState<'monthly' | 'running'>('monthly');
  const [hover, setHover] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(Math.max(280, el.clientWidth)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const points = useMemo(() => {
    let run = 0;
    let runDemand = 0;
    return s.monthly.map((m) => {
      run += m.amount;
      runDemand += m.demanded;
      return { ...m, value: m.isFuture ? 0 : mode === 'running' ? run : m.amount, demand: mode === 'running' ? runDemand : m.demanded };
    });
  }, [mode, s.monthly]);
  const fyLabel = useMemo(() => {
    const start = Number(s.monthly[0]?.key.slice(0, 4));
    return `FY ${start}-${String((start + 1) % 100).padStart(2, '0')}`;
  }, [s.monthly]);

  const W = width, H = 230, L = 50, R = 8, T = 12, B = 28;
  const max = Math.max(1, ...points.map((p) => Math.max(p.value, p.demand))) * 1.12;
  const step = niceStep(max / 4);
  const top = Math.ceil(max / step) * step;
  const y = (v: number) => T + (H - T - B) * (1 - v / top);
  const band = (W - L - R) / 12;
  const bw = Math.min(24, band * 0.52);
  const ticks: number[] = [];
  for (let v = 0; v <= top + 1e-6; v += step) ticks.push(v);
  const bar = (x: number, v: number) => {
    const h = Math.max(4, y(0) - y(v));
    return `M${x},${y(0)}v${-(h - 4)}q0,-4 4,-4h${bw - 8}q4,0 4,4v${h - 4}z`;
  };
  const hp = hover != null ? points[hover] : null;
  const total = s.monthly.reduce((sum, m) => sum + m.amount, 0);

  return (
    <Card index={index} label="Collections chart">
      <div className="ot-hd">
        <div>
          <h3>Collections, {fyLabel}</h3>
          <p>{inr(total)} received this financial year</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="ot-seg" role="group" aria-label="Chart view">
            <button aria-pressed={mode === 'monthly'} onClick={() => setMode('monthly')}>Monthly</button>
            <button aria-pressed={mode === 'running'} onClick={() => setMode('running')}>Running total</button>
          </div>
          <button className="ot-go" onClick={onOpen} aria-label="Open booking report"><Icon name="up" /></button>
        </div>
      </div>
      <div className="ot-legend" style={{ marginBottom: 6 }}>
        <span><i className="m-acc" />Received</span>
        <span><i style={{ background: 'var(--ot-ink)', height: 3, verticalAlign: 3 }} />Demanded by payment plans</span>
        <span><i className="m-hatch" />Expected, coming months</span>
      </div>
      <div className="ot-chart" ref={box} onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Customer receipts per month, ${fyLabel}`}>
          {ticks.map((v) => (
            <g key={v}>
              <line className="gl" x1={L} x2={W - R} y1={y(v)} y2={y(v)} />
              <text className="ax" x={L - 8} y={y(v) + 4} textAnchor="end">{v ? inr(v) : '0'}</text>
            </g>
          ))}
          {points.map((p, i) => {
            const cx = L + band * i + band / 2;
            const x = cx - bw / 2;
            return (
              <g key={p.key}>
                {p.isFuture ? (
                  p.demand > 0 ? <path className="b-exp" d={bar(x, p.demand)} /> : <rect className="b-empty" x={x} y={y(0) - 6} width={bw} height={6} rx={3} />
                ) : p.value > 0 ? (
                  <path className={p.isCurrent ? 'b-now' : 'b-got'} d={bar(x, p.value)} />
                ) : null}
                {!p.isFuture && p.demand > 0 ? <line className="tick" x1={x - 3} x2={x + bw + 3} y1={y(p.demand)} y2={y(p.demand)} /> : null}
                <text className={`ax ${p.isCurrent ? 'on' : ''}`} x={cx} y={H - 8} textAnchor="middle">{p.label}</text>
                <rect
                  className="hit"
                  x={L + band * i}
                  y={T}
                  width={band}
                  height={H - T - B}
                  rx={8}
                  tabIndex={0}
                  aria-label={`${p.label}: ${p.isFuture ? 'not started' : inr(p.value)}`}
                  onMouseEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                  onClick={() => setHover(i)}
                />
              </g>
            );
          })}
        </svg>
        {hp ? (
          <div className="ot-tip" style={{ left: Math.min(W - 170, Math.max(0, L + band * (hover as number) + band / 2 - 85)) }}>
            <span className="faint">{hp.label} {hp.key.slice(0, 4)}{hp.isCurrent ? ', to date' : ''}</span>
            <b>{hp.isFuture ? (hp.demand > 0 ? inr(hp.demand) : 'Nothing due') : inr(hp.value)}</b>
            <span className="muted">
              {hp.isFuture
                ? 'Expected from payment plans'
                : hp.demand > 0
                  ? `Demanded ${inr(hp.demand)} · ${Math.round((hp.value / hp.demand) * 100)}% in`
                  : mode === 'running' ? 'Received since 1 April' : 'Customer receipts, net of refunds'}
            </span>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

const STAGE_TONE: Record<UnitStage, string> = { available: 'mute', blocked: 'warn', form: 'warn', booked: 'acc', agreement: 'acc', registered: 'good' };
type UnitsFilter = 'all' | UnitStage | 'balance' | 'overdue' | 'anyBooked';
const BOOKED_STAGES: UnitStage[] = ['booked', 'agreement', 'registered'];
const FILTERS: Array<{ key: UnitsFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'anyBooked', label: 'All booked' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'balance', label: 'Balance due' },
  { key: 'booked', label: 'No agreement yet' },
  { key: 'agreement', label: 'Agreement done' },
  { key: 'registered', label: 'Sale deed done' },
  { key: 'form', label: 'Block' },
  { key: 'available', label: 'Available' },
];
const PAGE = 12;

export function UnitsCard({ rows, index, onOpenUnit, onOpenAll }: { rows: TodayUnitRow[]; index: number; onOpenUnit: (unitId: string) => void; onOpenAll: () => void }) {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<UnitsFilter>('all');
  const [shown, setShown] = useState(PAGE);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === 'anyBooked' ? !BOOKED_STAGES.includes(r.stage) : filter === 'overdue' ? r.overdue <= 0 : filter === 'balance' ? r.pending <= 0 : filter !== 'all' && r.stage !== filter) return false;
      return !needle || r.unitNo.toLowerCase().includes(needle) || r.buyer.toLowerCase().includes(needle);
    });
  }, [filter, q, rows]);
  useEffect(() => setShown(PAGE), [filter, q]);
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: rows.length, anyBooked: rows.filter((r) => BOOKED_STAGES.includes(r.stage)).length, balance: rows.filter((r) => r.pending > 0).length, overdue: rows.filter((r) => r.overdue > 0).length };
    rows.forEach((r) => { c[r.stage] = (c[r.stage] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <Card index={index} label="Units">
      <div className="ot-hd ot-hd-search">
        <div>
          <h3>Units</h3>
          <p>Payment and paperwork status for every unit</p>
        </div>
        <input className="ot-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search unit or buyer" aria-label="Search units" />
        <button className="ot-go" onClick={onOpenAll} aria-label="Open Units">
          <Icon name="up" />
        </button>
      </div>
      <div className="ot-filters">
        {FILTERS.filter((f) => f.key === 'all' || f.key === filter || (counts[f.key] || 0) > 0).map((f) => (
          <button key={f.key} className="ot-pill" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label} <span className="num" style={{ opacity: 0.7 }}>{counts[f.key] || 0}</span>
          </button>
        ))}
      </div>
      <div className="ot-tbl-wrap">
        <table>
          <thead>
            <tr><th>Unit</th><th>Buyer</th><th className="r">Value</th><th className="r">Balance</th><th>Paid</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {filtered.slice(0, shown).map((r) => (
              <tr key={r.unitId}>
                <td><span className="ot-unit">{r.unitNo}</span></td>
                <td>{r.buyer || <span className="faint">{r.stage === 'available' || r.stage === 'blocked' ? 'Not booked' : 'Name not saved'}</span>}</td>
                <td className="r num">{r.value ? inr(r.value) : <span className="faint">No price</span>}</td>
                <td className="r num">{r.pending > 0 ? <b style={{ fontWeight: 600 }}>{inr(r.pending)}</b> : <span className="faint">0</span>}</td>
                <td>
                  {r.stage === 'available' || r.stage === 'blocked' ? <span className="faint">-</span> : (
                    <div className="ot-pbar">
                      <div className="ot-meter"><i className={r.paidPct >= 100 ? 'm-good' : 'm-acc'} style={{ width: `${r.paidPct}%` }} /></div>
                      <span className="num faint">{r.paidPct}%</span>
                    </div>
                  )}
                </td>
                <td>
                  {r.overdue > 0 ? (
                    <span className="ot-chip c-bad" title={`Oldest unpaid demand ${r.overdueDays} days ago`}>{inr(r.overdue)} overdue</span>
                  ) : (
                    <span className={`ot-chip c-${STAGE_TONE[r.stage]}`}>{STAGE_LABEL[r.stage]}</span>
                  )}
                  {r.nextStep ? <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>Next: {r.nextStep}</div> : null}
                </td>
                <td className="r"><button className="ot-btn sm" onClick={() => onOpenUnit(r.unitId)}>Open</button></td>
              </tr>
            ))}
            {!filtered.length ? (
              <tr><td colSpan={7} className="faint" style={{ padding: '22px 6px' }}>No units match. Clear the search or pick another filter.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {filtered.length > shown ? (
        <button className="ot-btn sm ot-more" onClick={() => setShown((n) => n + PAGE * 2)}>
          Show more ({filtered.length - shown} left)
        </button>
      ) : null}
    </Card>
  );
}

export function InventoryCard({ s, index, onOpen }: { s: TodaySummary; index: number; onOpen: () => void }) {
  const inv = s.inventory;
  const seg: Array<[string, number, string]> = [
    ['Sale deed done', inv.registered, 'm-ink'],
    ['Agreement done', inv.agreement, 'm-acc'],
    ['Booked, no agreement', inv.booked, 'm-hatch'],
    ['Block, not booked', inv.form, 'm-warn'],
    ['Available', inv.available, 'm-soft'],
    ['On hold', inv.blocked, 'm-bad'],
  ];
  return (
    <Card index={index} label="Inventory">
      <Head title="Inventory" sub={`${inv.total} units in this project`} onOpen={onOpen} />
      <div className="ot-statrow" style={{ marginBottom: 14 }}>
        <div><div className="ot-lbl">Booked</div><div className="ot-mid"><CountUp value={s.bookedUnits} format={(v) => String(Math.round(v))} /><span className="faint" style={{ fontSize: 16 }}> / {inv.total}</span></div></div>
        <div><div className="ot-lbl">Available</div><div className="ot-mid num">{inv.available + inv.form}</div></div>
        <div><div className="ot-lbl">Unsold value</div><div className="ot-mid num">{inr(s.unsoldValue)}</div></div>
      </div>
      <div className="ot-meter" style={{ height: 16 }}>
        {seg.map(([label, n, cls]) => (n > 0 ? <i key={label} className={cls} style={{ width: `${(n / Math.max(1, inv.total)) * 100}%` }} title={`${label}: ${n}`} /> : null))}
      </div>
      <div className="ot-legend" style={{ marginTop: 12 }}>
        {seg.filter(([, n]) => n > 0).map(([label, n, cls]) => (
          <span key={label}><i className={cls} />{label} <b className="num" style={{ color: 'var(--ot-ink)', fontWeight: 600 }}>{n}</b></span>
        ))}
      </div>
    </Card>
  );
}

export function StatutoryCard({ s, index }: { s: TodaySummary; index: number }) {
  return (
    <Card index={index} label="GST and RERA">
      <Head title="GST and RERA" sub="Statutory dates and GST from buyers" />
      <div className="ot-kv"><span className="muted">GST billable on booked units</span><b className="num">{inr(s.gst.due)}</b></div>
      <div className="ot-kv"><span className="muted">GST collected from buyers</span><b className="num">{inr(s.gst.collected)}</b></div>
      <div className="ot-kv"><span className="muted">Units with GST pending</span>{s.gst.unitsPending ? <span className="ot-chip c-warn">{s.gst.unitsPending} units</span> : <span className="ot-chip c-good">None</span>}</div>
      <div className="ot-kv"><span className="muted">Next GSTR-3B</span><span className="mono">{shortDate(s.dates.gstr3b)}</span></div>
      <div className="ot-kv"><span className="muted">RERA quarterly update</span><span className="mono">{shortDate(s.dates.rera)}</span></div>
    </Card>
  );
}

const CO_TONE: Record<string, string> = { urgent: 'bad', high: 'warn', medium: 'acc', low: 'mute' };
export function ChangeOrdersCard({ s, index, onOpen }: { s: TodaySummary; index: number; onOpen: () => void }) {
  const co = s.changeOrders;
  return (
    <Card index={index} label="Change orders">
      <Head title="Change orders" sub={`${co.open} open · ${co.waitingBuyer} waiting for the buyer`} onOpen={onOpen} />
      {co.top.length ? (
        <div className="ot-list">
          {co.top.map((c) => (
            <div className="ot-item" key={c.id}>
              {c.unitNo ? <span className="ot-unit">{c.unitNo}</span> : null}
              <div className="bd"><div className="t">{c.title || c.coNo || 'Change order'}</div></div>
              <span className={`ot-chip c-${CO_TONE[String(c.priority)] || 'mute'}`}>
                {c.customerApproval === 'pending' ? 'Buyer to approve' : String(c.priority || 'open').replace(/^./, (m) => m.toUpperCase())}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="faint" style={{ margin: 0, fontSize: 13 }}>No open change orders. New requests from buyers will show here.</p>
      )}
    </Card>
  );
}

export function ConstructionCard({ index }: { index: number }) {
  return (
    <Card className="ghost" index={index} label="Construction progress">
      <div className="ot-hd">
        <div><h3>Construction progress</h3><p>Slab by slab, per tower</p></div>
        <span className="ot-chip c-mute">Coming later</span>
      </div>
      <div className="ot-empty-slab">{[60, 85, 40, 95, 70, 30, 50].map((h, i) => <i key={i} style={{ height: `${h}%` }} />)}</div>
      <div className="faint" style={{ fontSize: 13 }}>This card fills in once stage tracking is added. It will drive stage-linked demand letters and the RERA quarterly update.</div>
    </Card>
  );
}

const dayMonth = (ms: number) => {
  const d = new Date(ms);
  return { day: String(d.getDate()).padStart(2, '0'), month: d.toLocaleDateString('en-IN', { month: 'short' }) };
};
const relDays = (ms: number) => {
  const today = new Date();
  const days = Math.round((ms - new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()) / 86400000);
  return days <= 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days} days`;
};

const ACTION_ICON: Record<TodayAction['kind'], keyof typeof ICONS> = { call: 'phone', loan: 'bank', workflow: 'doc', changeOrders: 'tool', materials: 'truck' };
const ACTION_TONE: Record<TodayAction['kind'], string> = { call: 'bad', loan: 'warn', workflow: 'acc', changeOrders: 'bad', materials: 'acc' };

export function ActionsCard({ actions, index, onAction }: { actions: TodayAction[]; index: number; onAction: (a: TodayAction) => void }) {
  const [done, setDone] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setDone((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const open = actions.length - actions.filter((a) => done.has(a.id)).length;
  return (
    <Card index={index} label="Today's actions">
      <div className="ot-hd">
        <div><h3>Today's actions</h3><p>{actions.length ? `${open} open · tick when done` : 'Built from dues, workflows, loans and orders'}</p></div>
        {actions.length ? <span className="ot-chip c-acc">{actions.length - open} of {actions.length} done</span> : null}
      </div>
      {actions.length ? (
        <>
          <div className="ot-meter thin" style={{ margin: '-4px 0 10px' }}><i className="m-good" style={{ width: `${((actions.length - open) / actions.length) * 100}%` }} /></div>
          <div className="ot-list">
            {actions.map((a) => (
              <div className={`ot-item ${done.has(a.id) ? 'done' : ''}`} key={a.id}>
                <button className="ot-check" onClick={() => toggle(a.id)} aria-label={`Mark done: ${a.title}`} aria-pressed={done.has(a.id)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true"><path d="m5 12 5 5 9-10" /></svg>
                </button>
                <div className={`ot-ibox c-${ACTION_TONE[a.kind]}`}><Icon name={ACTION_ICON[a.kind]} /></div>
                <div className="bd">
                  <div className="t">{a.title}</div>
                  {a.sub ? <div className="s">{a.sub}</div> : null}
                </div>
                <div className="ot-item-end">
                  <span className={`ot-chip c-${a.chip[0]}`}>{a.chip[1]}</span>
                  <button className="ot-btn sm" onClick={() => onAction(a)}>{a.cta}</button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="faint" style={{ margin: 0, fontSize: 13 }}>Nothing needs you right now. Overdue buyers, ready workflow steps, stuck loans, urgent change orders and late deliveries show up here.</p>
      )}
    </Card>
  );
}

export function OverdueNote({ s }: { s: TodaySummary }) {
  return s.dues.noPlanUnits ? <span className="faint" style={{ fontSize: 12 }}>{s.dues.noPlanUnits} booked units have no payment plan and are left out.</span> : null;
}

type PayTab = 'overdue' | 'week' | 'today';
export function PaymentsCard({ s, index, onOpenUnit }: { s: TodaySummary; index: number; onOpenUnit: (unitId: string) => void }) {
  // The Overdue tab only appears when something is overdue.
  const hasOverdue = s.dues.overdueRows.length > 0;
  const [picked, setTab] = useState<PayTab>('overdue');
  const tab: PayTab = picked === 'overdue' && !hasOverdue ? 'week' : picked;
  const [shown, setShown] = useState(8);
  useEffect(() => setShown(8), [tab]);
  const today: PaymentRow[] = s.receivedToday.rows.map((r) => ({ unitId: r.unitId, unitNo: r.unitNo, buyer: r.buyer, label: 'Receipt', amount: r.amount, ms: r.ms }));
  const rows = tab === 'overdue' ? s.dues.overdueRows : tab === 'week' ? s.dues.weekRows : today;
  const tabs = ([['overdue', 'Overdue', s.dues.overdueRows.length], ['week', 'Due in 7 days', s.dues.weekRows.length], ['today', 'Received today', today.length]] as Array<[PayTab, string, number]>).filter(([k]) => k !== 'overdue' || hasOverdue);
  const time = (ms: number) => new Date(ms).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  return (
    <Card index={index} label="Payments">
      <Head title="Payments" sub="Who to call, who is due, what came in" />
      <div className="ot-tabs" role="tablist">
        {tabs.map(([k, label, n]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
            {label} <span className={`ot-chip ${k === 'overdue' && n ? 'c-bad' : 'c-mute'}`} style={{ padding: '1px 7px' }}>{n}</span>
          </button>
        ))}
      </div>
      <div className="ot-tbl-wrap">
        <table>
          <thead>
            <tr><th>Unit</th><th>Buyer</th><th>{tab === 'today' ? 'Type' : 'For'}</th><th className="r">Amount</th><th>{tab === 'overdue' ? 'Late by' : tab === 'week' ? 'Due on' : 'Posted'}</th><th /></tr>
          </thead>
          <tbody>
            {rows.slice(0, shown).map((r, i) => (
              <tr key={`${r.unitId}:${i}`}>
                <td><span className="ot-unit">{r.unitNo}</span></td>
                <td>{r.buyer || <span className="faint">Name not saved</span>}</td>
                <td className="muted">{r.label}</td>
                <td className="r num"><b style={{ fontWeight: 600 }}>{inr(r.amount)}</b></td>
                <td>{tab === 'overdue' ? <span className="ot-chip c-bad">{r.days} days</span> : <span className="mono faint">{tab === 'week' ? shortDate(new Date(r.ms)) : time(r.ms)}</span>}</td>
                <td className="r"><button className="ot-btn sm" onClick={() => onOpenUnit(r.unitId)}>Open</button></td>
              </tr>
            ))}
            {!rows.length ? (
              <tr><td colSpan={6} className="faint" style={{ padding: '22px 6px' }}>
                {tab === 'overdue' ? 'No overdue payments on units with a payment plan.' : tab === 'week' ? 'Nothing falls due in the next 7 days.' : 'No receipts posted yet today.'}
              </td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {rows.length > shown ? <button className="ot-btn sm ot-more" onClick={() => setShown((n) => n + 16)}>Show more ({rows.length - shown} left)</button> : null}
      {tab !== 'today' ? <div style={{ marginTop: 10 }}><OverdueNote s={s} /></div> : null}
    </Card>
  );
}

export function UpcomingCard({ s, index, onOpenUnit }: { s: TodaySummary; index: number; onOpenUnit: (unitId: string) => void }) {
  return (
    <Card index={index} label="Upcoming">
      <Head title="Upcoming" sub="Next dues and compliance dates" />
      <div className="ot-list">
        {s.upcoming.map((u, i) => {
          const dm = dayMonth(u.ms);
          const isStat = !u.unitId;
          return (
            <div className="ot-item" key={`${u.label}:${i}`}>
              <div className="ot-datechip"><small>{dm.month}</small><b className="num">{dm.day}</b></div>
              <div className="bd">
                <div className="t">{isStat ? u.label : `${u.label}, ${u.unitNo}`}</div>
                <div className="s">{isStat ? 'Statutory date' : `${u.buyer || 'Buyer'} · ${inr(u.amount)}`}</div>
              </div>
              {isStat ? <span className="ot-chip c-mute">{relDays(u.ms)}</span> : (
                <button className="ot-chip c-acc" onClick={() => onOpenUnit(u.unitId)} title="Open unit">{relDays(u.ms)}</button>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export function LoansCard({ s, index }: { s: TodaySummary; index: number }) {
  const l = s.loans;
  const pctDisbursed = l.expected > 0 ? Math.min(100, (l.disbursed / l.expected) * 100) : 0;
  return (
    <Card index={index} label="Home loans">
      <Head title="Home loans" sub={l.needed ? `${l.needed} buyers need a loan` : 'No buyer has marked a loan as needed'} />
      {l.needed ? (
        <>
          <div className="ot-statrow" style={{ marginBottom: 14 }}>
            <div><div className="ot-lbl">Sanctioned</div><div className="ot-mid num">{l.sanctioned}</div></div>
            <div><div className="ot-lbl">In process</div><div className="ot-mid num">{l.inProcess}</div></div>
            <div><div className="ot-lbl">Not started</div><div className="ot-mid num" style={{ color: l.notStarted ? 'var(--ot-bad-ink)' : undefined }}>{l.notStarted}</div></div>
          </div>
          <div className="ot-lbl" style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span>Disbursed to us</span>
            <span><b className="num" style={{ color: 'var(--ot-ink)' }}>{inr(l.disbursed)}</b>{l.expected ? ` of ${inr(l.expected)}` : ''}</span>
          </div>
          <div className="ot-meter" style={{ marginTop: 6 }}><i className="m-good" style={{ width: `${pctDisbursed}%` }} /></div>
          {l.banks.length ? (
            <div style={{ marginTop: 14 }}>
              {l.banks.slice(0, 4).map(([bank, n]) => (
                <div className="ot-kv" key={bank}><span className="muted">{bank}</span><b className="num">{n} file{n === 1 ? '' : 's'}</b></div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}

function AttentionUnitCarousel({ unitIds, unitRows, onOpenUnit, label }: {
  unitIds: string[]; unitRows: TodayUnitRow[]; onOpenUnit: (unitId: string) => void; label: string;
}) {
  const unitNoById = useMemo(() => new Map(unitRows.map((row) => [row.unitId, row.unitNo])), [unitRows]);
  return (
    <div className="ot-attention-units" aria-label={`${label} units`} tabIndex={0}>
      {unitIds.map((id) => <button key={id} className="ot-unit ot-attention-unit" onClick={() => onOpenUnit(id)} title={`Open ${unitNoById.get(id) || id}`}>
        {unitNoById.get(id) || id}
      </button>)}
    </div>
  );
}

export function AttentionCard({ s, index, onOpenUnits, onOpenUnit }: { s: TodaySummary; index: number; onOpenUnits: () => void; onOpenUnit: (unitId: string) => void }) {
  const a = s.attention;
  const items: Array<{ title: string; sub: string; unitIds: string[] }> = [];
  if (a.noPlanUnits) items.push({ title: `${a.noPlanUnits} units have no payment plan`, sub: 'Overdue and due amounts can\'t be worked out for them. Add a plan in the booking.', unitIds: a.noPlanUnitIds });
  if (a.agreementLate) items.push({ title: `${a.agreementLate} unit${a.agreementLate === 1 ? '' : 's'} over 10% paid without an agreement`, sub: 'RERA allows at most 10% of the price before the sale agreement is registered.', unitIds: a.agreementLateUnitIds });
  if (a.notStarted) items.push({ title: `${a.notStarted} bookings with no workflow`, sub: 'Their office workflow was never started, so no steps are tracked.', unitIds: a.notStartedUnitIds });
  if (a.loansNotStarted) items.push({ title: `${a.loansNotStarted} loan files not started`, sub: 'The buyer needs a loan but no loan workflow exists yet.', unitIds: a.loansNotStartedUnitIds });
  return (
    <Card index={index} label="Needs your attention">
      <div className="ot-hd"><div><h3 style={{ color: items.length ? 'var(--ot-bad-ink)' : undefined }}>Needs your attention</h3><p>Gaps that will cost money or a deadline</p></div></div>
      {items.length ? (
        <div className="ot-list">
          {items.map(({ title, sub, unitIds }) => (
            <div className="ot-item" key={title} style={{ alignItems: 'flex-start' }}>
              <div className="ot-ibox c-bad"><Icon name="alert" /></div>
              <div className="bd">
                <div className="t" style={{ whiteSpace: 'normal' }}>{title}</div>
                <div className="s" style={{ whiteSpace: 'normal' }}>{sub}</div>
                {unitIds.length ? <AttentionUnitCarousel unitIds={unitIds} unitRows={s.unitRows} onOpenUnit={onOpenUnit} label={title} /> : null}
              </div>
            </div>
          ))}
          <button className="ot-btn sm" style={{ marginTop: 6, alignSelf: 'flex-start' }} onClick={onOpenUnits}>Open units</button>
        </div>
      ) : (
        <p className="faint" style={{ margin: 0, fontSize: 13 }}>No gaps found. Every booking has a payment plan, a workflow and its paperwork on time.</p>
      )}
    </Card>
  );
}

export function WorkflowCard({ s, index, onStep }: { s: TodaySummary; index: number; onStep: (unitId: string, action: DrawerActionType | null) => void }) {
  const w = s.workflow;
  const rowById = useMemo(() => new Map(s.unitRows.map((r) => [r.unitId, r])), [s.unitRows]);
  const busiest = useMemo(() => {
    let best = 0;
    w.steps.forEach((st, i) => { if (st.unitIds.length > (w.steps[best]?.unitIds.length || 0)) best = i; });
    return best;
  }, [w.steps]);
  const [view, setView] = useState<'journey' | 'tasks'>('journey');
  const [picked, setPicked] = useState<string>(() => w.steps[busiest]?.key || 'all_done');
  const buckets = [...w.steps.map((st) => ({ key: st.key, title: st.title, unitIds: st.unitIds })), { key: 'all_done', title: 'All steps done', unitIds: w.allDone }];
  const current = buckets.find((b) => b.key === picked) || buckets[0];
  const unitButton = (unitId: string, step?: PendingStep) => {
    const r = rowById.get(unitId);
    return (
      <button key={unitId + (step?.stepKey || '')} className="ot-unitbtn" onClick={() => onStep(unitId, step?.action?.type || null)} title={step?.action ? `${step.action.label} for ${r?.unitNo}` : `Open ${r?.unitNo}`}>
        <span className="ot-unit">{r?.unitNo || unitId}</span>
        {step?.action ? <span>{step.action.label}</span> : null}
      </button>
    );
  };
  if (!w.hasDefinition) {
    return (
      <Card className="panel" index={index} label="Booking workflow">
        <Head title="Booking workflow" sub="No office workflow is set for this project yet. Pick one in office settings to track each booking's steps here." />
      </Card>
    );
  }
  return (
    <Card className="panel" index={index} label="Booking workflow">
      <div className="ot-hd">
        <div>
          <h3>Booking workflow</h3>
          <p>{w.readyCount} steps ready · {w.blockedCount} waiting on another step{w.notStarted.length ? ` · ${w.notStarted.length} not started` : ''}</p>
        </div>
        <div className="ot-seg dark" role="group" aria-label="Workflow view">
          <button aria-pressed={view === 'journey'} onClick={() => setView('journey')}>By step</button>
          <button aria-pressed={view === 'tasks'} onClick={() => setView('tasks')}>Ready tasks</button>
        </div>
      </div>
      {view === 'journey' ? (
        <>
          <div className="ot-steps" style={{ ['--n' as any]: buckets.length }}>
            {buckets.map((b, i) => (
              <button key={b.key} className={`ot-step ${b.key === 'all_done' ? 'done' : ''} ${i === busiest && b.key !== 'all_done' && b.unitIds.length ? 'hot' : ''}`} aria-pressed={picked === b.key} onClick={() => setPicked(b.key)}>
                <span className="node num">{b.unitIds.length}</span>
                <span className="lab">{b.title}</span>
              </button>
            ))}
          </div>
          <div className="ot-step-units">
            <span className="muted" style={{ fontSize: 13, marginRight: 4 }}>{current.title}:</span>
            {current.unitIds.length ? current.unitIds.slice(0, 24).map((id) => unitButton(id, w.firstOpenByUnit.get(id))) : <span className="muted" style={{ fontSize: 13 }}>No units here.</span>}
            {current.unitIds.length > 24 ? <span className="muted" style={{ fontSize: 13 }}>+{current.unitIds.length - 24} more</span> : null}
          </div>
        </>
      ) : (
        <div className="ot-list">
          {w.tasks.length ? w.tasks.slice(0, 8).map((t) => (
            <div className="ot-item" key={t.key} style={{ alignItems: 'flex-start' }}>
              <div className="bd">
                <div className="t">{t.title} <span className="ot-chip c-mute" style={{ marginLeft: 6 }}>{t.steps.length}</span></div>
                <div className="ot-step-units" style={{ border: 0, padding: 0, marginTop: 8 }}>
                  {t.steps.slice(0, 12).map((st) => unitButton(st.unitId, st))}
                  {t.steps.length > 12 ? <span className="muted" style={{ fontSize: 13 }}>+{t.steps.length - 12} more</span> : null}
                </div>
              </div>
            </div>
          )) : <p className="muted" style={{ margin: 0, fontSize: 13 }}>No steps are ready right now.</p>}
        </div>
      )}
    </Card>
  );
}

export function CostCard({ s, costs, index, onOpenBills, onOpenEstimate }: { s: TodaySummary; costs: CostSummary; index: number; onOpenBills: () => void; onOpenEstimate: () => void }) {
  const outMonth = costs.materials.month + costs.salaries.monthly;
  const inMonth = s.receivedThisMonth;
  const max = Math.max(1, inMonth, outMonth);
  const hasData = costs.materials.billCount > 0 || costs.salaries.staff > 0;
  return (
    <Card index={index} label="Construction cost">
      <Head title="Construction cost" sub="Money going out against money coming in" onOpen={onOpenBills} />
      {hasData ? (
        <>
          <div className="ot-statrow" style={{ marginBottom: 14 }}>
            <div><div className="ot-lbl">Material bills, all time</div><div className="ot-mid"><CountUp value={costs.materials.total} /></div></div>
            <div><div className="ot-lbl">This financial year</div><div className="ot-mid num">{inr(costs.materials.fy)}</div></div>
          </div>
          <div className="ot-lbl" style={{ marginBottom: 6 }}>This month</div>
          <div className="ot-inout">
            <span className="muted">In</span>
            <div className="ot-meter"><i className="m-good" style={{ width: `${(inMonth / max) * 100}%` }} /></div>
            <b className="num">{inr(inMonth)}</b>
            <span className="muted">Out</span>
            <div className="ot-meter"><i className="m-ink" style={{ width: `${(outMonth / max) * 100}%` }} /></div>
            <b className="num">{inr(outMonth)}</b>
          </div>
          <div style={{ marginTop: 12 }}>
            <div className="ot-kv"><span className="muted">Material bills this month</span><b className="num">{inr(costs.materials.month)} · {costs.materials.monthCount} bill{costs.materials.monthCount === 1 ? '' : 's'}</b></div>
            <div className="ot-kv"><span className="muted">Last month</span><b className="num">{inr(costs.materials.prev)}</b></div>
            <div className="ot-kv"><span className="muted">Salaries on payroll, per month</span><b className="num">{inr(costs.salaries.monthly)} · {costs.salaries.staff} staff</b></div>
            {costs.topVendors.map(([v, amt]) => (
              <div className="ot-kv" key={v}><span className="faint">{v}</span><span className="num">{inr(amt)}</span></div>
            ))}
          </div>
        </>
      ) : (
        <p className="faint" style={{ margin: 0, fontSize: 13 }}>No vendor bills or payroll staff yet. Bills entered in Orders and Receipts, and staff added in Payroll, show here.</p>
      )}
      <button className="ot-btn sm" style={{ marginTop: 12 }} onClick={onOpenEstimate}>Compare with estimate</button>
    </Card>
  );
}

type BoundaryProps = { name: string; children: React.ReactNode };
/** Keeps one broken card from taking the whole page down. */
export class CardBoundary extends React.Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(err: unknown) {
    console.error(`Office Today: ${this.props.name} card failed`, err);
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <section className="ot-card" aria-label={this.props.name}>
        <div className="ot-hd"><div><h3>{this.props.name}</h3><p>This card couldn't load. The rest of the page is fine.</p></div></div>
        <button className="ot-btn sm" onClick={() => this.setState({ failed: false })}>Try again</button>
      </section>
    );
  }
}
